// Validator for the `privateData.tax` shape defined in
// docs/dac7-private-data-schema.md. Pure JS (no Zod) to match codebase style.
//
// validateTaxInfo(record) → { valid: boolean, errors: string[] }
// Errors are stable string codes that the form layer can map to i18n keys.

const IDENTIFIER_TYPES = ['HETU', 'YTUNNUS', 'OTHER_TIN'];
const NAME_FORMS = ['INDIVIDUAL', 'ENTITY'];
const VAT_STATUSES = ['REGISTERED', 'EXEMPT_SMALL_BUSINESS', 'INVOICING_SERVICE'];
const { EU_VAT_AREA } = require('./vat/regions');

const isString = v => typeof v === 'string' && v.length > 0;
const isStringOrNull = v => v === null || typeof v === 'string';
const isIso2 = v => isString(v) && /^[A-Z]{2}$/.test(v);
const isHetuPlain = v => /^[0-9]{6}[+\-A-FU-Y]\d{3}[0-9A-Z]$/.test(v);
const isYtunnusPlain = v => /^\d{7}-\d$/.test(v);
const isFiPostcode = v => /^\d{5}$/.test(v);

// Normalised VAT number: country prefix plus 2–12 alphanumerics, no spaces.
const VAT_NUMBER_RE = /^[A-Z]{2}[A-Z0-9]{2,12}$/;
const isVatNumber = v => isString(v) && VAT_NUMBER_RE.test(v);
// A non-EU seller's home tax id (US EIN, Swiss UID, UK VAT…): any 2–20
// letters and digits once spaces, dots and hyphens are stripped. The EU
// format above applies inside FI/AX/EU only.
// homeVat.label is printed on receipts next to the rate ("VAT", "GST"…).
const HOME_VAT_LABEL_MAX = 20;
const HOME_TAX_ID_RE = /^[A-Z0-9]{2,20}$/;
const normalizeTaxId = v => (typeof v === 'string' ? v.replace(/[\s.-]/g, '').toUpperCase() : v);
const isHomeTaxId = v => isString(v) && HOME_TAX_ID_RE.test(normalizeTaxId(v));

const isEncryptedBlob = v => {
  if (!v || typeof v !== 'object') return false;
  if (typeof v.v !== 'number') return false;
  if (!isString(v.ciphertext)) return false;
  if (!isString(v.iv)) return false;
  if (!isString(v.authTag)) return false;
  return true;
};

const validateIdentifier = (identifier, errors) => {
  if (!identifier || typeof identifier !== 'object') {
    errors.push('identifier.missing');
    return;
  }
  if (!IDENTIFIER_TYPES.includes(identifier.type)) {
    errors.push('identifier.type.invalid');
  }
  if (identifier.type === 'YTUNNUS') {
    if (!isYtunnusPlain(identifier.plaintext)) {
      errors.push('identifier.ytunnus.invalid');
    }
    if (identifier.encrypted !== null && identifier.encrypted !== undefined) {
      errors.push('identifier.ytunnus.encryptedNotAllowed');
    }
  } else if (identifier.type === 'HETU' || identifier.type === 'OTHER_TIN') {
    // Plaintext path is allowed only as a transient state during migration.
    // Persisted records must have an encrypted blob.
    if (!isEncryptedBlob(identifier.encrypted) && !isString(identifier.plaintext)) {
      errors.push('identifier.encrypted.missing');
    }
  }
  if (!isIso2(identifier.issuedBy)) {
    errors.push('identifier.issuedBy.invalid');
  }
  if (identifier.last4 !== null && identifier.last4 !== undefined && !isString(identifier.last4)) {
    errors.push('identifier.last4.invalid');
  }
};

const validateName = (name, errors) => {
  if (!name || typeof name !== 'object') {
    errors.push('name.missing');
    return;
  }
  if (!NAME_FORMS.includes(name.form)) {
    errors.push('name.form.invalid');
    return;
  }
  if (name.form === 'INDIVIDUAL') {
    if (!isString(name.firstName)) errors.push('name.firstName.missing');
    if (!isString(name.lastName)) errors.push('name.lastName.missing');
  }
  if (name.form === 'ENTITY') {
    if (!isString(name.legalName)) errors.push('name.legalName.missing');
  }
};

const validateAddress = (address, errors) => {
  if (!address || typeof address !== 'object') {
    errors.push('address.missing');
    return;
  }
  if (!isString(address.street)) errors.push('address.street.missing');
  if (!isString(address.city)) errors.push('address.city.missing');
  if (!isIso2(address.country)) errors.push('address.country.invalid');
  if (!isString(address.postcode)) {
    errors.push('address.postcode.missing');
  } else if (address.country === 'FI' && !isFiPostcode(address.postcode)) {
    errors.push('address.postcode.invalidFi');
  }
};

const validateBirthInfo = (birthInfo, errors) => {
  if (!birthInfo || typeof birthInfo !== 'object') {
    errors.push('birthInfo.missing');
    return;
  }
  if (!isString(birthInfo.date) || !/^\d{4}-\d{2}-\d{2}$/.test(birthInfo.date)) {
    errors.push('birthInfo.date.invalid');
  }
  if (!isString(birthInfo.city)) errors.push('birthInfo.city.missing');
  if (!isIso2(birthInfo.country)) errors.push('birthInfo.country.invalid');
};

// The schema v2 VAT block. A v1 record may carry it (the migration writes
// the block without bumping the version); a v2 record must.
const validateVatBlock = (record, errors) => {
  const status = record.vatStatus;
  if (status === null || status === undefined) {
    if (record.schemaVersion >= 2) errors.push('vatStatus.missing');
    return;
  }
  if (!VAT_STATUSES.includes(status)) {
    errors.push('vatStatus.invalid');
    return;
  }
  const country = record.address && record.address.country;
  const insideEu = EU_VAT_AREA.includes(country) || country === 'AX';
  const vatNumberAbsent =
    record.vatNumber === null || record.vatNumber === undefined || record.vatNumber === '';
  const vatNumberOk = insideEu ? isVatNumber : isHomeTaxId;
  if (!vatNumberAbsent && !vatNumberOk(record.vatNumber)) {
    errors.push('vatNumber.invalid');
  }
  if (status === 'REGISTERED') {
    if (insideEu) {
      if (vatNumberAbsent) {
        errors.push('vatNumber.missing');
      }
    } else {
      const h = record.homeVat;
      const rateOk = h && typeof h.rate === 'number' && h.rate >= 0 && h.rate <= 1;
      if (!rateOk || !isString(h.label) || h.label.length > HOME_VAT_LABEL_MAX) {
        errors.push('homeVat.invalid');
      }
    }
  }
  if (status === 'EXEMPT_SMALL_BUSINESS' && !insideEu) {
    errors.push('vatStatus.exemptOutsideEu');
  }
  if (status === 'INVOICING_SERVICE') {
    const s = record.invoicingService;
    if (!s || !isString(s.name)) {
      errors.push('invoicingService.missing');
    } else {
      const serviceVatNumberAbsent =
        s.vatNumber === null || s.vatNumber === undefined || s.vatNumber === '';
      if (!serviceVatNumberAbsent && !isVatNumber(s.vatNumber)) {
        errors.push('invoicingService.vatNumber.invalid');
      }
    }
  }
  if (record.exemptId !== null && record.exemptId !== undefined && !isString(record.exemptId)) {
    errors.push('exemptId.invalid');
  }
  if (
    record.vatStatusHistory !== null &&
    record.vatStatusHistory !== undefined &&
    !Array.isArray(record.vatStatusHistory)
  ) {
    errors.push('vatStatusHistory.invalid');
  }
};

const validateTaxInfo = record => {
  const errors = [];
  if (!record || typeof record !== 'object') {
    return { valid: false, errors: ['tax.missing'] };
  }
  if (record.schemaVersion !== 1 && record.schemaVersion !== 2) {
    errors.push('schemaVersion.invalid');
  }
  // Outside-EU sellers fall out of DAC7. v1 kept a minimal marker record;
  // v2 records also carry name, address and the VAT block (spec §2).
  if (record.residency?.outsideEu === true) {
    if (record.schemaVersion >= 2) {
      validateName(record.name, errors);
      validateAddress(record.address, errors);
      validateVatBlock(record, errors);
    }
    if (!isString(record.submittedAt)) errors.push('submittedAt.missing');
    return { valid: errors.length === 0, errors };
  }
  if (
    !record.residency ||
    !Array.isArray(record.residency.countryCodes) ||
    record.residency.countryCodes.length === 0
  ) {
    errors.push('residency.missing');
  } else if (!record.residency.countryCodes.every(isIso2)) {
    errors.push('residency.invalid');
  }
  validateIdentifier(record.identifier, errors);
  validateName(record.name, errors);
  validateAddress(record.address, errors);
  validateVatBlock(record, errors);

  // birthInfo is required only when there is no identifier
  const hasIdentifier =
    record.identifier &&
    (isEncryptedBlob(record.identifier.encrypted) || isString(record.identifier.plaintext));
  if (!hasIdentifier) {
    validateBirthInfo(record.birthInfo, errors);
  }

  if (record.vat !== null && record.vat !== undefined && !isString(record.vat)) {
    errors.push('vat.invalid');
  }
  if (!isString(record.submittedAt)) {
    errors.push('submittedAt.missing');
  }

  return { valid: errors.length === 0, errors };
};

module.exports = {
  validateTaxInfo,
  validateVatBlock,
  isVatNumber,
  isHomeTaxId,
  isHetuPlain,
  isYtunnusPlain,
  isFiPostcode,
  isIso2,
  IDENTIFIER_TYPES,
  NAME_FORMS,
  VAT_STATUSES,
};
